import { ChangeDetectionStrategy, Component, ResourceRef, inject, resource } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { UserForm } from '@lib/user-form';
import { HumanizedUser, UserTable } from '@lib/user-table';
import { Auth, UserService } from 'src/app/service';

import { Roles, Teams, UserStates } from '../schemas';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, MatToolbarModule, MatDialogModule, UserTable],
  template: `
    <mat-toolbar>
      <h1>User Accounts</h1>
      <span class="spacer"></span>
      <div class="cell-actions">
        @if (auth.isAdmin()) {
          <button matMiniFab matTooltip="Add User" (click)="showForm()">
            <mat-icon>add</mat-icon>
          </button>
        }

        <button matIconButton matTooltip="Reload users" (click)="reload()">
          <mat-icon [class.rotating]="pending()">refresh</mat-icon>
        </button>
      </div>
    </mat-toolbar>

    <lcm-user-table [users]="users()" [pending]="pending()" (edited)="reload()" (removed)="reload()" />
  `,
})
export class UsersPage {
  userService = inject(UserService);
  auth = inject(Auth);
  dialog = inject(MatDialog);

  private usersResource: ResourceRef<HumanizedUser[]> = resource({
    params: () => ({ users: this.userService.users() }),
    defaultValue: [],
    loader: async ({ params }) => {
      const users = params.users;

      // Map enum values to human-readable labels using lookup objects
      const result = users.map((user) => ({
        ...user,
        HumanRole: Roles[user.Role],
        HumanTeam: Teams[user.Team],
        HumanState: UserStates[user.State],
      }));
      return result;
    },
  });

  users = this.usersResource.value;
  pending = this.usersResource.isLoading;

  reload(): void {
    this.userService.reload();
  }

  showForm() {
    this.dialog
      .open(UserForm)
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reload(),
      });
  }
}
